// frontend/src/admin/v2/SecretsList.jsx
import { useMemo, useState } from 'react'
import {
  Stack, Box, Typography, Chip, Button, TextField, Alert, Skeleton,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import IntegrationMonogram from './components/IntegrationMonogram'
import { useSecrets } from './hooks/useSecrets'
import { SP, FS, hoverOnly } from '../../theme/tokens'
import Panel from '../../components/common/Panel'
import ListToolbar from '../../components/common/ListToolbar'
import FilterPillRow from '../../components/common/FilterPillRow'
import EmptyState from '../../components/common/EmptyState'
import RefreshButton from '../../components/common/RefreshButton'
import useListQuery from '../../components/common/listQuery/useListQuery'
import { etikett, ton, zustandswert } from './adminStatus'

/**
 * SecretsList — /admin/secrets page.
 *
 * @description
 *   Inventory of every secret env var declared in
 *   backend/integrations/registry.yaml. Status pill rules:
 *     - red: env var missing
 *     - amber: never rotated, OR rotated >180 days ago
 *     - green: present + rotated within 180 days
 *
 *   Values are NEVER displayed in full — only the last 4 chars are
 *   surfaced for staff to spot the wrong-key case (e.g. "did I deploy
 *   the prod or dev key?").
 *
 *   🔴 **Das Wort fuer `red` ist hier „Missing", nicht „Failing"** — rot heisst
 *   auf dieser Flaeche „die Env-Variable fehlt", und „Failing" waere eine
 *   Aussage ueber einen Dienst, den es nicht gibt. Das Wort ist ein Parameter
 *   an `etikett` (`adminStatus.js`), keine zweite Abbildung.
 *
 *   🔴 **Keine Suche** (Plan §2.3): 16 Env-Variablen an 11 Integrationen — die
 *   Schwelle des Konzepts liegt bei 25. Kommt die Registry darueber, ist die
 *   Suche eine Wiedervorlage (`§7 Q4`).
 */

const ROT = 'Missing'

/** Die drei Filterwerte — einer je Serverstatus, disjunkt.
 *
 * 🔴 Der WERT ist das Wort (`healthy`/`watch`/`missing`), nicht das Servertoken
 * (Plan §3.1, R16 C-1) — `zustandswert` leitet ihn aus demselben Etikett ab,
 * das die Karte rendert. */
const ZUSTAENDE = ['green', 'amber', 'red'].map(status => ({
  status,
  value: zustandswert(status, { red: ROT }),
  label: etikett(status, { red: ROT }),
}))


function SecretRow({ secret, onMarkRotated }) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleConfirm = async () => {
    setSubmitting(true)
    try {
      await onMarkRotated(secret.env_var_name, note.trim() || null)
      setDialogOpen(false)
      setNote('')
    } finally {
      setSubmitting(false)
    }
  }

  const rotatedLine = secret.rotated_at
    ? (secret.days_since_rotation === 0
        ? 'Rotated today'
        : `Rotated ${secret.days_since_rotation}d ago`)
    : 'Never rotated'

  const farbe = ton(secret.status)

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      spacing={SP.card}
      sx={{ py: SP.card, px: SP.tight, borderRadius: 1, ...hoverOnly({ bgcolor: 'action.hover' }) }}
    >
      <IntegrationMonogram
        letter={secret.monogram_letter}
        tint={secret.monogram_tint}
        size={32}
      />
      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
        <Stack direction="row" alignItems="baseline" spacing={SP.tight} flexWrap="wrap">
          <Typography sx={{ fontSize: FS.sm, fontWeight: 500, fontFamily: 'monospace' }}>
            {secret.env_var_name}
          </Typography>
          <Chip
            data-testid={`karte-status-${secret.env_var_name}`}
            label={etikett(secret.status, { red: ROT })}
            size="small"
            sx={{
              height: 18, fontSize: '0.65rem', fontWeight: 600,
              color: farbe, bgcolor: 'transparent',
              border: 1, borderColor: farbe,
            }}
          />
        </Stack>
        <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
          {secret.integration_name}
          {secret.present
            ? <> · value …{secret.last4 || '????'}</>
            : <> · <Box component="span" sx={{ color: 'error.main' }}>not set on this instance</Box></>}
          {' · '}{rotatedLine}
        </Typography>
      </Stack>
      <Tooltip title="Record that this secret was rotated today">
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
            onClick={() => setDialogOpen(true)}
            sx={{ fontSize: FS.xs, alignSelf: { xs: 'flex-start', sm: 'center' } }}
          >
            Mark rotated
          </Button>
        </span>
      </Tooltip>

      <Dialog open={dialogOpen} onClose={() => !submitting && setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
          Mark <Box component="span" sx={{ fontFamily: 'monospace' }}>{secret.env_var_name}</Box> as rotated
        </DialogTitle>
        <DialogContent>
          <Stack spacing={SP.card} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              This logs today's date as the most-recent rotation for this env var.
              Records an entry in <Box component="span" sx={{ fontFamily: 'monospace' }}>secrets_rotation_log</Box>.
            </Typography>
            <TextField
              label="Note (optional)"
              size="small"
              fullWidth
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. rotated after suspected leak; previous key revoked at provider"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={submitting}>
            {submitting ? 'Saving…' : 'Confirm rotation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}


export default function SecretsList() {
  const { data, loading, error, errorTitle, markRotated, refetch } = useSecrets()

  const dimensionen = useMemo(() => [{
    key: 'status',
    kind: 'quick',
    label: 'State',
    values: ZUSTAENDE.map(z => ({ value: z.value, label: z.label })),
  }], [])
  const query = useListQuery({ dimensions: dimensionen })
  const { state, set, clearAll, chips } = query

  const secrets = useMemo(() => data?.secrets || [], [data])

  const zaehler = useMemo(() => {
    const z = Object.fromEntries(ZUSTAENDE.map(zz => [zz.value, 0]))
    secrets.forEach((s) => {
      const wert = zustandswert(s.status, { red: ROT })
      if (z[wert] != null) z[wert] += 1
    })
    return z
  }, [secrets])

  const gewaehlt = useMemo(
    () => (Array.isArray(state.status) ? state.status : []),
    [state.status],
  )
  const gefiltert = useMemo(
    () => (gewaehlt.length === 0
      ? secrets
      : secrets.filter(s => gewaehlt.includes(zustandswert(s.status, { red: ROT })))),
    [secrets, gewaehlt],
  )

  // Gruppierung bleibt (Plan §1.3) — leere Gruppen fallen mit dem Filter weg.
  const groups = useMemo(() => gefiltert.reduce((acc, s) => {
    (acc[s.integration_key] = acc[s.integration_key] || []).push(s)
    return acc
  }, {}), [gefiltert])

  const sperre = Boolean(error) && !data
  const leerGrund = sperre
    ? 'error'
    : (data && secrets.length === 0)
      ? 'no-data'
      : (data && gefiltert.length === 0)
        ? 'no-match'
        : null

  const quickReihe = (
    <Box>
      <FilterPillRow
        alwaysShow
        multi
        label="State"
        options={ZUSTAENDE.map(z => ({ ...z, count: data ? zaehler[z.value] : undefined }))}
        value={gewaehlt}
        onChange={(v) => set('status', v)}
        disabled={!data}
      />
    </Box>
  )

  const leerzustand = leerGrund ? (
    <Box sx={{ py: SP.lg }}>
      <EmptyState
        reason={leerGrund}
        message={
          leerGrund === 'error'
            ? 'Could not load the secrets list.'
            : leerGrund === 'no-data'
              // 🔴 R16 W-8: kein Repo-Pfad in der Oberflaeche — ein Satz,
              // WANN Eintraege entstehen.
              ? 'No secrets registered. Entries appear here once an integration declares an environment variable.'
              : 'No secrets match this filter.'
        }
        action={
          // 🔴 R16 W-6: nur wenn eine Pille gesetzt ist (Konzept §8.1).
          leerGrund === 'no-match' && gewaehlt.length > 0
            ? <Button variant="outlined" onClick={clearAll}>Clear all filters</Button>
            : undefined
        }
        onRetry={leerGrund === 'error' ? refetch : undefined}
        // 🔴 R16 W-2/F-3: der Env-Hinweis (403) als ZWEITE Detailzeile.
        detail={
          leerGrund === 'error'
            ? (
              <>
                {error}
                {errorTitle && <><br />{errorTitle}</>}
              </>
            )
            : undefined
        }
      />
    </Box>
  ) : null

  return (
    <Stack spacing={SP.section}>
      {error && !sperre && (
        <Alert severity="error" variant="outlined" title={errorTitle || undefined}>{error}</Alert>
      )}

      <Panel pad="card" data-testid="liste-panel" aria-busy={loading || undefined}>
        <ListToolbar
          quickFilters={quickReihe}
          // 🔴 R16 F-1/W-1: der Zaehler bleibt in `no-data`/`no-match` stehen;
          // nur der Fehler hat keinen (Hausform `ListenMuster.jsx:705`).
          count={leerGrund === 'error' ? undefined : {
            noun: 'secrets',
            n: data ? gefiltert.length : null,
            m: data?.summary?.total ?? null,
          }}
          refresh={(
            <RefreshButton
              ariaLabel="Refresh secrets list"
              loading={loading}
              firstLoad={!data}
              onClick={refetch}
            />
          )}
          query={{ chips, clearAll }}
        >
          {query.ignoredValues.length > 0 && (
            <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mt: SP.tight }}>
              {`Ignored unknown filter value “${query.ignoredValues[0].value}”.`}
            </Alert>
          )}

          {/* Der Schutzsatz bleibt sichtbar — er ist die Begruendung, warum hier
              nur vier Zeichen stehen (Plan §0 Nr. 2). 🔴 R16 W-5: nur wenn
              Zeilen da sind — ueber „No secrets registered." erklaert er eine
              Maskierung, die es nicht zu sehen gibt. */}
          {!leerGrund && (
            <Alert severity="info" variant="outlined" sx={{ fontSize: FS.xs, mt: SP.tight }}>
              Names + last-4 only. Full secret values never leave the server.
              Stale = rotated &gt;180 days ago (or never).
            </Alert>
          )}

          {loading && !data ? (
            <Box data-testid="liste-skelett" sx={{ mt: SP.tight }}>
              <Stack spacing={1}>
                {[0, 1, 2, 3, 4, 5].map(i => <Skeleton key={i} variant="rounded" height={56} />)}
              </Stack>
            </Box>
          ) : leerGrund ? (
            leerzustand
          ) : (
            <Stack data-testid="liste-karte" spacing={SP.card} sx={{ mt: SP.tight }}>
              {Object.entries(groups).map(([integrationKey, items]) => (
                <Panel key={integrationKey} pad="card">
                  <Stack spacing={0.25}>
                    <Typography variant="caption" sx={{
                      fontSize: FS.xs, color: 'text.secondary',
                      textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
                    }}>
                      {items[0]?.integration_name || integrationKey}
                    </Typography>
                  </Stack>
                  <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
                    {items.map(s => (
                      <SecretRow key={s.env_var_name} secret={s} onMarkRotated={markRotated} />
                    ))}
                  </Stack>
                </Panel>
              ))}
            </Stack>
          )}
        </ListToolbar>
      </Panel>
    </Stack>
  )
}
